import deepmerge from 'deepmerge'

import type { RawThemeConfig } from '@/themeConfig/types'

export const black = 'black'
export const white = 'white'
export const darkGray = '#222222'
export const mediumGray = '#555555'
export const lightGray = '#DDE0E5'
export const veryLightGray = '#f7f7f7'

const primaryColor = 'var(--primary-color)'
const pageBackgroundColor = 'var(--page-background-color)'
const primaryColorDarker = 'var(--primary-color-darker)'

export function globalDefaults(): Partial<RawThemeConfig> {
  return {
    dialog: {
      backgroundColor: ['#F7F9FA', 'var(--page-background-color-lighter)'],
      headerTextColor: ['black', 'white'],
      headerBackground: ['white', transparentWhite(0.05)],
      headerIconColor: ['#555555', transparentWhite(0.25)],
      headerBorder: ['1px solid #DDE0E6', '1px solid rgba(255, 255, 255, 0.1)'],
    },
    calendar: {
      backgroundColor: [transparentWhite(0.1), 'rgba(34, 36, 51, 0.1)'],
      buttonColor: ['#ffffff', transparentWhite(0.05)],
      buttonBorder: ['1px solid #cccccc', `1px solid ${transparentWhite(0.1)}`],
      buttonIconColor: ['#282B37', transparentWhite(0.87)],
      dayTextColor: ['#282B37', transparentWhite(0.87)],
      availabilityTextColor: ['#525a6c', transparentWhite(0.6)],
      priceTextColor: ['#282B37', transparentWhite(0.6)],
      todayBackgroundColor: ['#ddd', transparentWhite(0.2)],
      disabledDayTextColor: ['#BFC1CA', transparentWhite(0.3)],
      disabledDayBackgroundColor: [transparentBlack(0.03), transparentWhite(0.03)],
      soldOutDayTextColor: ['#FF3552', '#FF3552'],
      soldOutDayBackgroundColor: ['#FFF5F7', '#330b10'],
      hoverDayBackgroundColor: [transparentBlack(0.01), transparentWhite(0.03)],

      divider: ['1px dashed #cccccc', `1px dashed ${transparentWhite(0.1)}`],
      cellBorder: ['1px solid #cccccc', `1px solid ${transparentWhite(0.1)}`],
      monthTextColor: ['#282B37', transparentWhite(0.87)],
      weekdayTextColor: ['#525A6C', transparentWhite(0.87)],
      legendTextColor: ['#525A6C', transparentWhite(0.6)],
      legendMobileBackgroundColor: ['transparent', transparentWhite(0.03)],
    },
    mobileFooter: {
      backgroundColor: ['#ffffff', pageBackgroundColor],
      textColor: ['#545557', transparentWhite(0.6)],
      cartButtonBackgroundColor: ['#ffffff', pageBackgroundColor],
      cartButtonHoverBackgroundColor: ['#ffffff', pageBackgroundColor],
      cartButtonBorder: ['2px solid #BFC1CA', `2px solid ${transparentWhite(0.2)}`],
      cartButtonIconColor: ['#747981', '#ffffff'],
      cartBadgeBackgroundColor: '#ff0000',
      cartBadgeTextColor: '#ffffff',
      priceTextColor: ['#545557', transparentWhite(0.87)],
    },
    dashedDividerColor: [lightGray, transparentWhite(0.1)],
    cancelFreeTicketsBackgroundColor: [lightGray, transparentWhite(0.1)],
    buttonTextTransform: 'none',
  }
}

export function ticketureBrandThemeConfig(): RawThemeConfig {
  return {
    tier: 1,
    primaryColor: '#1D72C7',
    primaryContrastColor: 'white',
    secondaryColor: '#1D72C7',
    headerBackground: '#003567',
    memberLinksColor: 'white',
    memberLinksColorHover: '#1D72C7',
    cartHeaderBackgroundColor: '#003567',
    footerBackgroundColor: '#003567',
    footerTextColor: 'white',
    pageBackgroundColor: '#FFFFFF',
    headerDropShadow: `0px 0px 5px 0px ${transparentBlack(0.4)}`,
    logoHeightDesktop: '32px',
    headerHeightDesktop: '105px',
    linkColor: '#1D72C7',
    linkHoverColor: '#0a4d8f',
    errorColor: '#E1121C',
  }
}

export function unbrandedFallbackThemeConfig(): Partial<RawThemeConfig> {
  const fallbackConfig = {
    primaryColor: darkGray,
    secondaryColor: darkGray,
    messageColor: darkGray,
    labelColor: [darkGray, 'white'],
    headerBackground: ['white', 'transparent'],
    memberLinksColor: [darkGray, 'white'],
    memberLinksColorHover: [primaryColorDarker, transparentWhite(0.5)],
    logoHeight: '32px',
    footerBackgroundColor: ['white', transparentWhite(0.09)],
    footerTextColor: [mediumGray, transparentWhite(0.6)],
    bodyColor: ['#141414', transparentWhite(0.6)],
    headingColor: ['black', transparentWhite(0.87)],
    pageBackgroundColor: ['white', 'black'],
    headerTopBorder: 'none',
    headerBottomBorder: 'none',
    headerDropShadow: 'none',
    headerHeight: '105px',
    secondaryButtonStyle: 'outline',
    cartBorder: ['1px solid var(--light-gray)', `1px solid ${transparentWhite(0.1)}`],
    infoColor: '#1F78D1',
    successColor: ['#127A3C', '#009000'],
    warningColor: '#B95D00',
    errorColor: '#D92B0E',
    promoColor: '#9456C1',
    primaryFont: 'proxima-nova, Arial, sans-serif',
    secondaryFont: 'proxima-nova, Arial, sans-serif',
    externalFontsStylesheets: ['https://use.typekit.net/tsu4pxd.css'],
    selectArrowColor: ['var(--primary-color)', 'white'],
    borderRadius: '4px',
    controlBorderRadius: '4px',
    cart: {
      headerBackgroundColor: [darkGray, transparentWhite(0.13)],
      headerTextColor: transparentWhite(0.87),
      headerBorder: 'none',
      backgroundColor: ['white', pageBackgroundColor],
      priceBackgroundColor: ['#f7f7f7', transparentWhite(0.06)],
      dividerColor: [lightGray, transparentWhite(0.13)],
      removeButtonBackgroundHoverColor: [veryLightGray, transparentWhite(0.06)],
    },
    backButton: {
      iconColor: '#282B37',
      backgroundColor: 'white',
      shadow: `0px 0px 5px ${transparentBlack(0.4)}`,
    },
    steppers3: {
      iconColor: [mediumGray, 'white'],
      border: [`2px solid ${lightGray}`, `2px solid ${transparentWhite(0.18)}`],
      buttonBackgroundColor: ['white', transparentWhite(0.06)],
      buttonBackgroundHoverColor: ['white', transparentWhite(0.15)],
      borderHoverColor: ['#bfc1ca', transparentWhite(0.5)],
      disabledIconColor: ['var(--light-gray)', transparentWhite(0.2)],
      count: {
        backgroundColor: [lightGray, 'transparent'],
        border: [`2px solid ${lightGray}`, `2px solid ${transparentWhite(0.18)}`],
      },
    },
    radioButton: {
      borderColor: ['var(--medium-gray)', transparentWhite(0.18)],
      backgroundColor: ['white', transparentWhite(0.06)],
      borderWidth: '2px',
      checked: {
        backgroundColor: ['white', 'transparent'],
        borderColor: primaryColor,
      },
    },
    editOrder: {
      sessionBorderColor: ['#dde0e5', transparentWhite(0.09)],
      ticketBackgroundColor: ['#eff0f5', transparentWhite(0.06)],
    },
    divider: [`${lightGray} 1px solid`, `${transparentWhite(0.1)} 1px solid`],
    cancelFreeTicketsBackgroundColor: ['var(--light-gray)', transparentWhite(0.1)],
  }

  return deepmerge(globalDefaults(), fallbackConfig)
}

function transparentWhite(decimal: number): string {
  return `rgba(255, 255, 255, ${decimal})`
}

function transparentBlack(decimal: number): string {
  return `rgba(0, 0, 0, ${decimal})`
}
